import React, { useState } from "react";
import { Save, Lock, Trash2, Eye, EyeOff, Mail } from "lucide-react";
import { useAuth } from "../../context/AuthContext";
import { useNavigate } from "react-router-dom";
import { auth } from "../../lib/firebase";
import { Panel, Overline } from "../common/Panel";
import { toast } from "sonner";

const inputCls = "w-full bg-[var(--surface2)] border border-[var(--bd)] rounded-md px-3 py-2 sm:py-2.5 text-xs sm:text-sm focus:outline-none focus:border-[var(--accent)] transition-colors";

function isPasswordProvider() {
  return auth.currentUser?.providerData?.some((p) => p.providerId === "password") ?? false;
}

function PwField({ placeholder, value, onChange, show, onToggle }) {
  return (
    <div className="relative">
      <input
        type={show ? "text" : "password"}
        className={inputCls + " pr-10"}
        placeholder={placeholder}
        value={value}
        onChange={onChange}
      />
      <button type="button" onClick={onToggle}
        className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--t2)] hover:text-[var(--t1)] transition-colors">
        {show ? <EyeOff size={14} /> : <Eye size={14} />}
      </button>
    </div>
  );
}

export default function ProfileTab() {
  const { user, updateDisplayName, changePassword, deleteAccount, deleteAccountGoogle } = useAuth();
  const nav = useNavigate();
  const isPassword = isPasswordProvider();
  const initials = (user?.name || user?.email || "U").trim().slice(0, 2).toUpperCase();

  const [name, setName]               = useState(user?.name || "");
  const [nameLoading, setNameLoading] = useState(false);

  const [curPw, setCurPw]         = useState("");
  const [newPw, setNewPw]         = useState("");
  const [confirmPw, setConfirmPw] = useState("");
  const [showCur, setShowCur]     = useState(false);
  const [showNew, setShowNew]     = useState(false);
  const [pwLoading, setPwLoading] = useState(false);

  const [delPw, setDelPw]           = useState("");
  const [showDelPw, setShowDelPw]   = useState(false);
  const [delConfirm, setDelConfirm] = useState(false);
  const [delLoading, setDelLoading] = useState(false);

  const saveName = async () => {
    if (!name.trim()) { toast.error("Name cannot be empty"); return; }
    setNameLoading(true);
    try {
      await updateDisplayName(name.trim());
      toast.success("Display name updated");
    } catch { toast.error("Failed to update name"); }
    finally { setNameLoading(false); }
  };

  const savePw = async (e) => {
    e.preventDefault();
    if (newPw !== confirmPw) { toast.error("New passwords do not match"); return; }
    if (newPw.length < 6)   { toast.error("Password must be at least 6 characters"); return; }
    setPwLoading(true);
    try {
      await changePassword(curPw, newPw);
      toast.success("Password updated");
      setCurPw(""); setNewPw(""); setConfirmPw("");
    } catch (e) {
      toast.error(e.code === "auth/wrong-password" ? "Current password is incorrect" : "Failed to change password");
    } finally { setPwLoading(false); }
  };

  const handleDelete = async () => {
    setDelLoading(true);
    try {
      if (isPassword) { await deleteAccount(delPw); }
      else            { await deleteAccountGoogle(); }
      toast.success("Account deleted");
      nav("/");
    } catch (e) {
      toast.error(e.code === "auth/wrong-password" ? "Password is incorrect" : "Failed to delete account");
    } finally { setDelLoading(false); setDelConfirm(false); }
  };

  return (
    <div className="w-full space-y-4 sm:space-y-5 lg:space-y-6">

      {/* Page heading */}
      <div>
        <Overline>Account</Overline>
        <h2 className="font-heading font-bold text-lg sm:text-xl lg:text-2xl tracking-tight mt-1">My Profile</h2>
      </div>

      {/* Two-column grid on large screens */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-5 lg:gap-6 items-start">

        {/* Left: Identity card */}
        <Panel className="p-4 sm:p-5 lg:p-6">

          {/* Avatar row */}
          <div className="flex items-center gap-3 sm:gap-4">
            <div className="w-10 h-10 sm:w-14 sm:h-14 lg:w-16 lg:h-16 rounded-full overflow-hidden border border-[var(--bd)] shrink-0 bg-[var(--accent)] flex items-center justify-center text-black font-bold text-sm sm:text-lg lg:text-xl">
              {user?.photoURL
                ? <img src={user.photoURL} alt="avatar" className="w-full h-full object-cover" />
                : <span>{initials}</span>}
            </div>
            <div className="min-w-0 flex-1">
              <p className="font-heading font-bold text-sm sm:text-base lg:text-lg truncate">{user?.name}</p>
              <p className="text-[10px] sm:text-xs text-[var(--t2)] font-mono truncate">{user?.email}</p>
              {!isPassword && (
                <span className="inline-block mt-1 text-[9px] sm:text-[10px] px-1.5 sm:px-2 py-0.5 rounded-full border border-[var(--bd)] text-[var(--t2)]">
                  Google account
                </span>
              )}
            </div>
          </div>

          {/* Display name edit */}
          <div className="mt-4 sm:mt-6 pt-4 sm:pt-6 border-t border-[var(--bd)] space-y-2 sm:space-y-3">
            <Overline>Display Name</Overline>
            <div className="flex gap-2">
              <input
                className={inputCls}
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="Your name"
              />
              <button
                onClick={saveName}
                disabled={nameLoading || name.trim() === user?.name}
                className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-md bg-[var(--accent)] text-black text-xs sm:text-sm font-medium active:scale-95 transition-transform disabled:opacity-50 shrink-0 whitespace-nowrap"
              >
                <Save size={13} className="sm:w-3.5 sm:h-3.5" />
                {nameLoading ? "Saving..." : "Save"}
              </button>
            </div>

            {/* Email read-only row */}
            <div className="flex items-center gap-1.5 sm:gap-2 text-[var(--t2)] flex-wrap">
              <Mail size={12} className="shrink-0 sm:w-3.5 sm:h-3.5" />
              <span className="font-mono text-[10px] sm:text-xs truncate min-w-0">{user?.email}</span>
              <span className="text-[9px] sm:text-[10px] border border-[var(--bd)] rounded px-1 sm:px-1.5 py-0.5 shrink-0">read-only</span>
            </div>
          </div>
        </Panel>

        {/* Right: Change password + Danger zone stacked */}
        <div className="space-y-4 sm:space-y-5 lg:space-y-6">

          {/* Change password */}
          <Panel className="p-4 sm:p-5 lg:p-6">
            <Overline>Change Password</Overline>
            {isPassword ? (
              <form onSubmit={savePw} className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                <PwField placeholder="Current password"     value={curPw}     onChange={(e) => setCurPw(e.target.value)}     show={showCur}   onToggle={() => setShowCur((v) => !v)} />
                <PwField placeholder="New password"         value={newPw}     onChange={(e) => setNewPw(e.target.value)}     show={showNew}   onToggle={() => setShowNew((v) => !v)} />
                <input type="password" className={inputCls} placeholder="Confirm new password" value={confirmPw} onChange={(e) => setConfirmPw(e.target.value)} />
                <button
                  type="submit"
                  disabled={pwLoading || !curPw || !newPw || !confirmPw}
                  className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 lg:px-5 py-2 sm:py-2.5 rounded-md bg-[var(--accent)] text-black text-xs sm:text-sm font-medium active:scale-95 transition-transform disabled:opacity-50"
                >
                  <Lock size={13} className="sm:w-3.5 sm:h-3.5" />
                  {pwLoading ? "Updating..." : "Update Password"}
                </button>
              </form>
            ) : (
              <p className="text-xs sm:text-sm text-[var(--t2)] mt-2 sm:mt-3">
                Password management is handled by Google for this account. Visit your Google account settings to change it.
              </p>
            )}
          </Panel>

          {/* Danger zone */}
          <Panel className="p-4 sm:p-5 lg:p-6" style={{ borderColor: "color-mix(in srgb, var(--danger) 30%, transparent)" }}>
            <Overline style={{ color: "var(--danger)" }}>Danger Zone</Overline>
            <p className="text-xs sm:text-sm text-[var(--t2)] mt-1.5 sm:mt-2">
              Permanently delete this account and all associated data. This action cannot be undone.
            </p>

            {!delConfirm ? (
              <button
                onClick={() => setDelConfirm(true)}
                className="mt-3 sm:mt-4 inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-2 sm:py-2.5 rounded-md border border-[var(--danger)]/50 text-[var(--danger)] text-xs sm:text-sm font-medium hover:bg-[var(--danger)]/10 transition-colors"
              >
                <Trash2 size={13} className="sm:w-3.5 sm:h-3.5" /> Delete Account
              </button>
            ) : (
              <div className="mt-3 sm:mt-4 space-y-2 sm:space-y-3">
                <p className="text-xs sm:text-sm font-semibold text-[var(--danger)]">This is permanent. Are you sure?</p>
                {isPassword ? (
                  <PwField placeholder="Enter password to confirm" value={delPw} onChange={(e) => setDelPw(e.target.value)} show={showDelPw} onToggle={() => setShowDelPw((v) => !v)} />
                ) : (
                  <p className="text-[10px] sm:text-xs text-[var(--t2)] font-mono">A Google sign-in popup will appear to confirm your identity.</p>
                )}
                <div className="flex gap-2 flex-wrap">
                  <button
                    onClick={() => { setDelConfirm(false); setDelPw(""); }}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 rounded-md border border-[var(--bd)] text-xs sm:text-sm hover:border-[var(--bd-strong)] transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={delLoading || (isPassword && !delPw)}
                    className="inline-flex items-center gap-1 sm:gap-1.5 px-3 sm:px-4 py-1.5 sm:py-2 rounded-md bg-[var(--danger)] text-white text-xs sm:text-sm font-medium hover:opacity-90 transition-opacity disabled:opacity-50"
                  >
                    <Trash2 size={13} className="sm:w-3.5 sm:h-3.5" />
                    {delLoading ? "Deleting..." : "Delete permanently"}
                  </button>
                </div>
              </div>
            )}
          </Panel>

        </div>
      </div>

    </div>
  );
}
